<template>
  <div class="page">
    <main class="content">
      <section class="guides-section">
        <div class="guides-header">
          <h1 class="guides-title">Guides</h1>
          <p class="guides-subtitle">Find quick step-by-step guides to get the most out of Partycord.</p>
          <p class="guides-subtitle"><strong>This is a new addition. Not all guides have been uploaded and more will appear over time!</strong></p>

          <div class="guides-actions">
            <button class="support-button" @click="openSupportServer">
              <fa :icon="['fab', 'discord']" />
              Need Help? Join Support
            </button>
          </div>

          <div class="search-container">
            <svg class="search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              v-model="searchQuery"
              class="search-input"
              type="text"
              placeholder="Search guides..."
              aria-label="Search guides"
            >
            <button
              v-if="searchQuery"
              class="search-clear"
              @click="searchQuery = ''"
              aria-label="Clear guide search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <div class="results-info" v-if="searchQuery">
          {{ filteredGuides.length }} guide{{ filteredGuides.length === 1 ? '' : 's' }} found for "{{ searchQuery }}"
        </div>

        <div class="guides-grid" v-if="filteredGuides.length">
          <article v-for="guide in filteredGuides" :key="guide.slug" class="guide-card">
            <div class="guide-card-top">
              <p class="guide-difficulty">{{ guide.difficulty }}</p>
              <h2 class="guide-title">{{ guide.title }}</h2>
              <p class="guide-summary" v-html="renderInlineMarkdown(guide.summary)"></p>
            </div>

            <div class="guide-tags">
              <span v-for="tag in guide.tags" :key="`${guide.slug}-${tag}`" class="guide-tag">{{ tag }}</span>
            </div>

            <div class="guide-card-actions">
              <router-link :to="`/guides/${guide.slug}`" class="read-guide-link">Read Guide</router-link>
            </div>
          </article>
        </div>

        <div class="no-results" v-else>
          <p>No guides matched your search.</p>
          <button class="support-button" @click="openSupportServer">
            <fa :icon="['fab', 'discord']" />
            Ask for Help in Discord
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import guides from '../data/guides.json';
import { renderInlineMarkdown } from '../utils/markdown';

export default {
  name: 'GuidesPage',
  data() {
    return {
      searchQuery: '',
      guides
    };
  },
  computed: {
    filteredGuides() {
      const query = this.searchQuery.toLowerCase().trim();

      if (!query) {
        return this.guides;
      }

      return this.guides.filter((guide) => {
        const sectionContent = (guide.sections || []).flatMap((section) => {
          if (Array.isArray(section.content)) {
            return section.content;
          }

          return section.content ? [section.content] : [];
        });

        const searchable = [
          guide.title,
          guide.summary,
          guide.difficulty,
          ...(guide.tags || []),
          ...(guide.sections || []).map((section) => section.heading),
          ...(guide.sections || []).flatMap((section) => section.steps || []),
          ...sectionContent
        ]
          .join(' ')
          .toLowerCase();

        return searchable.includes(query);
      });
    }
  },
  methods: {
    renderInlineMarkdown,
    openSupportServer() {
      window.open('https://discord.com/invite/beaStFd', '_blank', 'noopener,noreferrer');
    }
  }
};
</script>

<style scoped>
.guides-section {
  width: 100%;
  max-width: var(--container-max-width);
}

.guides-header {
  text-align: center;
  margin-bottom: var(--spacing-xl);
}

.guides-title {
  font-size: clamp(2.5rem, 6vw, var(--font-size-display));
  margin-bottom: var(--spacing-sm);
  background: linear-gradient(135deg, #fdbb2d, #22c1c3);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.guides-subtitle {
  color: var(--color-text-secondary);
  margin: 0 auto var(--spacing-md) auto;
  font-size: var(--font-size-md);
}

.guides-actions {
  margin-bottom: var(--spacing-md);
}

.search-container {
  position: relative;
  max-width: 560px;
  margin: 0 auto;
}

.search-input {
  width: 100%;
  border: 2px solid transparent;
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-md) var(--spacing-md) var(--spacing-md) 3.2rem;
  background: var(--color-bg-tertiary);
  color: var(--color-text-primary);
  font-size: var(--font-size-sm);
  box-shadow: var(--shadow-md);
  transition: all var(--transition-normal);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-accent-discord);
  box-shadow: var(--shadow-lg), 0 0 20px rgba(88, 101, 242, 0.2);
}

.search-icon {
  width: 20px;
  height: 20px;
  position: absolute;
  top: 50%;
  left: 1.1rem;
  transform: translateY(-50%);
  color: var(--color-text-muted);
}

.search-clear {
  position: absolute;
  right: 0.8rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  padding: 0.25rem;
  cursor: pointer;
}

.search-clear svg {
  width: 18px;
  height: 18px;
}

.results-info {
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-md);
  text-align: center;
}

.guides-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--spacing-md);
}

.guide-card {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--border-radius-lg);
  padding: var(--spacing-md);
  text-align: left;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--spacing-md);
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
}

.guide-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg);
}

.guide-difficulty {
  color: #22c1c3;
  font-size: var(--font-size-xs);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.4rem;
}

.guide-title {
  font-size: 1.3rem;
  margin-bottom: 0.5rem;
  color: var(--color-text-primary);
}

.guide-summary {
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin-bottom: 0;
}

.guide-tags {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.guide-tag {
  background: rgba(34, 193, 195, 0.15);
  color: #7fe7e8;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  font-size: 0.8rem;
}

.guide-card-actions {
  margin-top: auto;
}

.read-guide-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  font-weight: 600;
  background: var(--color-accent-discord);
  color: var(--color-text-primary);
  border-radius: var(--border-radius-md);
  padding: 0.6rem 1rem;
  transition: all var(--transition-normal);
}

.read-guide-link:hover {
  background: var(--color-accent-discord-hover);
}

.support-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border: none;
  border-radius: var(--border-radius-md);
  background: var(--color-accent-discord);
  color: var(--color-text-primary);
  padding: 0.65rem 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.support-button:hover {
  background: var(--color-accent-discord-hover);
}

.no-results {
  text-align: center;
  color: var(--color-text-secondary);
  display: grid;
  gap: var(--spacing-sm);
  justify-items: center;
}

@media (max-width: 768px) {
  .guides-grid {
    grid-template-columns: 1fr;
  }
}
</style>
